import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  SharedSelection,
} from "@heroui/react";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdditionalNotes from "./additionalNotes";
import AvailableDaysSelector from "./availableDaySelector";
import FinancialInformation from "./financialInformation";
import LocationTravelAvailability from "./locationTravelAvailability";
import SportInformation from "./sportInformation";
import { useCreateEditReadyRoster } from "../hooks/useReadyRoster";
import {
  fetchCountries,
  fetchCurrencyOptions,
  fetchPurseOptions,
  fetchRosterStatus,
  fetchTrainingDisciplines,
  fetchWeightClasses
} from "../../../services/api";
import { ReadyRosterCardDto, MarketplaceCardResponseDto } from "../../../types/types";
import { formatEnumString, formatSportName, formatWeightClassName } from "../../../utils/utilMethods";

interface CreateEditReadyRosterProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: MarketplaceCardResponseDto;
  fetchData?: () => Promise<void>;
}

const CreateEditReadyRoster: React.FC<CreateEditReadyRosterProps> = ({
  isOpen,
  onClose,
  initialData,
  fetchData,
}) => {
  const {
    setPurse,
    validUpto,
    setValidUpto,
    setCurrency,
    setSport,
    preferredRuleSet,
    setPreferredRuleSet,
    setLocation,
    availableDays,
    setAvailableDays,
    notes,
    setNotes,
    setWeightClass,
    travelReady,
    setTravelReady,
    noticePeriodDays,
    setNoticePeriodDays,
    setRosterStatus,
    handleSave,
    resetForm,
  } = useCreateEditReadyRoster(initialData, fetchData);
  const [rosterStatusOptions, setRosterStatusOptions] = useState<string[]>([]);

  const [selectedRosterStatus, setSelectedRosterStatus] = useState("");
  const [selectedPurse, setSelectedPurse] = useState("");
  const [purses, setPurses] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sports, setSports] = useState<string[]>([]);
  const [selectedSport, setSelectedSport] = useState("");
  const [selectedWeight, setSelectedWeight] = useState("");
  const [weightClasses, setWeightClasses] = useState<string[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [modalCurrency, setModalCurrency] = useState("");
  const [locations, setLocations] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [dateRange, setDateRange] = useState<{
    start: CalendarDate | null;
    end: CalendarDate | null;
  }>({ start: null, end: null });
  const [errors, setErrors] = useState<{
    purse: string;
    currency: string;
    sport: string;
    preferredRuleSet: string;
    weightClass: string;
    validUpto: string;
    location: string;
    availableDays: string;
    noticePeriodDays: string;
    rosterStatus: string;
  }>({
    purse: "",
    currency: "",
    sport: "",
    preferredRuleSet: "",
    weightClass: "",
    validUpto: "",
    location: "",
    availableDays: "",
    noticePeriodDays: "",
    rosterStatus: "",
  });

  const isEditMode = !!initialData;
  const { t, i18n } = useTranslation('CreateEditReadyRoster');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const loadRosterStatusIfNeeded = async () => {
    if (rosterStatusOptions.length === 0) {
      try {
        setRosterStatusOptions(await fetchRosterStatus());
      } catch (e) {
        setError(t("failed_load_roster_statuses"));
      }
    }
  };

  const generateDateRange = (start: CalendarDate, end: CalendarDate): string[] => {
    const dates: string[] = [];
    let currentDate = start;

    while (currentDate.compare(end) <= 0) {
      const dateStr = `${currentDate.year}-${String(currentDate.month).padStart(2, "0")}-${String(currentDate.day).padStart(2, "0")}`;
      dates.push(dateStr);
      currentDate = currentDate.add({ days: 1 });
    }

    return dates;
  };
  const localDateToCalendarDate = (dateStr: string): CalendarDate => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new CalendarDate(year, month, day);
  };

  useEffect(() => {

    if (initialData && initialData.readyRosterCard?.availableDays && initialData.readyRosterCard?.availableDays.length > 0) {
      setAvailableDays(initialData.readyRosterCard?.availableDays);
      const sortedDates = [...initialData.readyRosterCard?.availableDays].sort();
      const startDate = localDateToCalendarDate(sortedDates[0]);
      const endDate = localDateToCalendarDate(sortedDates[sortedDates.length - 1]);

      setDateRange({ start: startDate, end: endDate });
    } else {
      setAvailableDays([]);
      setDateRange({ start: null, end: null });
    }
  }, [initialData, isOpen]);

  useEffect(() => {
    const loadEditDropdowns = async () => {
      if (initialData) {
        try {
          if (purses.length === 0) setPurses(await fetchPurseOptions());
          if (currencies.length === 0)
            setCurrencies(await fetchCurrencyOptions());
          if (sports.length === 0) setSports(await fetchTrainingDisciplines());
          if (weightClasses.length === 0)
            setWeightClasses(await fetchWeightClasses());
          if (locations.length === 0) setLocations(await fetchCountries());
          if (rosterStatusOptions.length === 0)
            setRosterStatusOptions(await fetchRosterStatus());

          setSelectedRosterStatus(initialData.readyRosterCard?.rosterStatus || "");
          setSelectedPurse(initialData.readyRosterCard?.purse || "");
          setModalCurrency(initialData.readyRosterCard?.currency || "");
          setSelectedSport(initialData.readyRosterCard?.sport || "");
          setSelectedWeight(initialData.readyRosterCard?.weightClass || "");
          setSelectedLocation(initialData.readyRosterCard?.location || "");
          setPreferredRuleSet(initialData.readyRosterCard?.preferredRuleSet || "");
          setNoticePeriodDays(initialData.readyRosterCard?.noticePeriodDays || 0);
          setTravelReady(initialData.readyRosterCard?.travelReady || false);
          setNotes(initialData.readyRosterCard?.notes || "");

          if (initialData.readyRosterCard?.validUpto) {
            setValidUpto(dayjs(initialData.readyRosterCard?.validUpto).toDate());
          }
        } catch (e) {
          setError(t("failed_load_dropdown_data"));
        }
      }
    };

    loadEditDropdowns();
    setErrors({
      purse: "",
      currency: "",
      sport: "",
      preferredRuleSet: "",
      weightClass: "",
      validUpto: "",
      location: "",
      availableDays: "",
      noticePeriodDays: "",
      rosterStatus: "",
    });
  }, [initialData, isOpen, t]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLocations(await fetchCountries());
        const userJson = localStorage.getItem("loginResponse");
        const user = userJson ? JSON.parse(userJson) : null;
      } catch (e) {
        setError(t("failed_load_initial_data"));
      }
    };
    loadInitialData();
  }, [t]);

  const resetModalState = () => {
    setDateRange({ start: null, end: null });
    setSelectedPurse("");
    setSelectedSport("");
    setSelectedWeight("");
    setSelectedRosterStatus("");
    setModalCurrency("");
    setSelectedLocation("");
    setPreferredRuleSet("");
    setTravelReady(false);
    setNoticePeriodDays(0);
    setNotes("");
    setValidUpto(null);
    setErrors({
      purse: "",
      currency: "",
      sport: "",
      preferredRuleSet: "",
      weightClass: "",
      validUpto: "",
      location: "",
      availableDays: "",
      noticePeriodDays: "",
      rosterStatus: "",
    });
    resetForm();
  };

  const validateForm = () => {
    const newErrors = {
      purse: selectedPurse ? "" : t("purse_required"),
      currency: modalCurrency ? "" : t("currency_required"),
      sport: selectedSport ? "" : t("sport_required"),
      preferredRuleSet: preferredRuleSet ? "" : t("level_required"),
      weightClass: selectedWeight ? "" : t("weight_class_required"),
      validUpto: validUpto
        ? dayjs(validUpto).isAfter(dayjs().utc().startOf("day"))
          ? ""
          : t("valid_upto_future")
        : t("valid_upto_required"),
      location: selectedLocation ? "" : t("location_required"),
      availableDays: availableDays.length > 0 ? "" : t("available_days_required"),
      noticePeriodDays:
        noticePeriodDays >= 0 ? "" : t("notice_period_required"),
      rosterStatus: selectedRosterStatus ? "" : t("roster_status_required"),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleCloseModal = () => {
    resetModalState();
    onClose();
  };

  const handleSaveWithReset = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      const result = await handleSave();
      if (result !== false) {
        resetModalState();
        onClose();
      }
    } catch (error) {
      setError(t("failed_save_roster"));
    }
  };

  const handleDateRangeChange = (range: { start: CalendarDate; end: CalendarDate } | null) => {
    if (range && range.start && range.end) {
      setDateRange(range);
      const generatedDates = generateDateRange(range.start, range.end);
      setAvailableDays(generatedDates);
      setErrors((prev) => ({
        ...prev,
        availableDays: generatedDates.length > 0 ? "" : t("available_days_required"),
      }));
    } else {
      setDateRange({ start: null, end: null });
      setAvailableDays([]);
      setErrors((prev) => ({
        ...prev,
        availableDays: t("available_days_required"),
      }));
    }
  };

  const loadPursesIfNeeded = async () => {
    if (purses.length === 0) {
      try {
        setPurses(await fetchPurseOptions());
      } catch (e) {
        setError(t("failed_load_purse_options"));
      }
    }
  };

  const loadSportsIfNeeded = async () => {
    if (sports.length === 0) {
      try {
        setSports(await fetchTrainingDisciplines());
      } catch (e) {
        setError(t("failed_load_training_disciplines"));
      }
    }
  };

  const loadWeightClassesIfNeeded = async () => {
    if (weightClasses.length === 0) {
      try {
        setWeightClasses(await fetchWeightClasses());
      } catch (e) {
        setError(t("failed_load_weight_classes"));
      }
    }
  };

  const loadCurrenciesIfNeeded = async () => {
    if (currencies.length === 0) {
      try {
        setCurrencies(await fetchCurrencyOptions());
      } catch (e) {
        setError(t("failed_load_currency_options"));
      }
    }
  };

  dayjs.extend(utc);

  const monthOptions = [
    { label: t("one_month"), months: 1 },
    { label: t("two_months"), months: 2 },
    { label: t("three_months"), months: 3 },
  ];

  const getClosestMonthOption = (validUpto: Date): string | undefined => {
    const diffMonths = dayjs(validUpto).diff(
      dayjs().utc().startOf("day"),
      "month",
      true
    );
    const thresholds = [
      { months: 1, range: [0.5, 1.5], key: "1" },
      { months: 2, range: [1.5, 2.5], key: "2" },
      { months: 3, range: [2.5, 3.5], key: "3" },
    ];
    const match = thresholds.find(
      ({ range }) => diffMonths >= range[0] && diffMonths <= range[1]
    );
    return match ? match.key : undefined;
  };

  const handleChange = (keys: SharedSelection) => {
    const selected = typeof keys === "string" ? keys : keys.currentKey;
    if (!selected) return;
    const monthsToAdd = Number(selected);

    const newDate = dayjs()
      .startOf("day")
      .add(monthsToAdd, "month")
      .toDate();

    setValidUpto(newDate);
    setErrors((prev) => ({ ...prev, validUpto: "" }));
  };

  const selectedKey = validUpto ? getClosestMonthOption(validUpto) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      isDismissable={false}
      scrollBehavior="inside"
      size="3xl"
      placement="center"
      classNames={{
        wrapper: "items-center justify-center p-2 sm:p-4",
        base: "bg-white rounded-3xl shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08)] w-full max-w-[calc(100vw-1rem)] sm:max-w-[780px] max-h-[92dvh] sm:max-h-[90vh] my-auto overflow-hidden font-['Plus_Jakarta_Sans',sans-serif]",
        header: "px-5 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b border-gray-100 shrink-0",
        body: "px-5 sm:px-8 py-4 sm:py-5 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]",
        footer: "px-5 sm:px-8 py-3.5 sm:pb-6 sm:pt-4 border-t border-gray-100 shrink-0",
      }}
    >
      <ModalContent>
        <>
          <ModalHeader>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans',sans-serif]">
                {isEditMode ? t("edit_title") : t("create_title")}
              </h2>
              <p className="text-xs sm:text-sm font-normal text-gray-500 mt-0.5 sm:mt-1 font-['Plus_Jakarta_Sans',sans-serif]">
                {isEditMode
                  ? t("edit_subtitle", "Update your ready roster details")
                  : t("create_subtitle", "Fill in your availability and fight preferences")}
              </p>
            </div>
          </ModalHeader>
          <ModalBody>
            {error && (
              <div className="text-red-500 text-sm mb-3 px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                {error}
              </div>
            )}
            {/* Stacked on mobile, side-by-side on large screens */}
            <div className="flex flex-col lg:flex-row gap-5 lg:gap-6">
              {/* Left column — main form sections */}
              <div className="flex-1 min-w-0 space-y-5">
                <FinancialInformation
                  t={t}
                  selectedPurse={selectedPurse}
                  setSelectedPurse={setSelectedPurse}
                  setPurse={setPurse}
                  purses={purses}
                  errors={errors}
                  setErrors={setErrors}
                  loadPursesIfNeeded={loadPursesIfNeeded}
                  formatSportName={formatSportName}
                  modalCurrency={modalCurrency}
                  setModalCurrency={setModalCurrency}
                  setCurrency={setCurrency}
                  currencies={currencies}
                  loadCurrenciesIfNeeded={loadCurrenciesIfNeeded}
                />
                <SportInformation
                  t={t}
                  selectedSport={selectedSport}
                  setSelectedSport={setSelectedSport}
                  setSport={setSport}
                  sports={sports}
                  errors={errors}
                  setErrors={setErrors}
                  loadSportsIfNeeded={loadSportsIfNeeded}
                  formatWeightClass={formatWeightClassName}
                  preferredRuleSet={preferredRuleSet}
                  setPreferredRuleSet={setPreferredRuleSet}
                  selectedWeight={selectedWeight}
                  setSelectedWeight={setSelectedWeight}
                  setWeightClass={setWeightClass}
                  weightClasses={weightClasses}
                  loadWeightClassesIfNeeded={loadWeightClassesIfNeeded}
                  selectedKey={selectedKey}
                  handleChange={handleChange}
                  monthOptions={monthOptions}
                />
                <LocationTravelAvailability
                  t={t}
                  selectedRosterStatus={selectedRosterStatus}
                  setSelectedRosterStatus={setSelectedRosterStatus}
                  setRosterStatus={setRosterStatus}
                  rosterStatusOptions={rosterStatusOptions}
                  errors={errors}
                  setErrors={setErrors}
                  loadRosterStatusIfNeeded={loadRosterStatusIfNeeded}
                  formatEnumString={formatEnumString}
                  selectedLocation={selectedLocation}
                  setSelectedLocation={setSelectedLocation}
                  setLocation={setLocation}
                  noticePeriodDays={noticePeriodDays}
                  setNoticePeriodDays={setNoticePeriodDays}
                  travelReady={travelReady}
                  setTravelReady={setTravelReady}
                />
              </div>
              {/* Right column — calendar & notes */}
              <div className="w-full lg:w-64 xl:w-72 shrink-0 space-y-5">
                <AvailableDaysSelector
                  t={t}
                  availableDays={availableDays}
                  dateRange={dateRange}
                  handleDateRangeChange={handleDateRangeChange}
                  errors={errors}
                  today={today}
                  getLocalTimeZone={getLocalTimeZone}
                />
                <AdditionalNotes
                  t={t}
                  notes={notes}
                  setNotes={setNotes}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="flex items-center justify-end gap-2.5 sm:gap-3">
            <Button
              variant="light"
              size="sm"
              onPress={handleCloseModal}
              className="flex-1 sm:flex-none px-4 sm:px-5 py-2 rounded-full text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 font-['Plus_Jakarta_Sans',sans-serif]"
            >
              {t("cancel")}
            </Button>
            <Button
              color="primary"
              size="sm"
              onPress={handleSaveWithReset}
              className="flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white font-['Plus_Jakarta_Sans',sans-serif]"
            >
              {isEditMode ? t("save_changes", "Save Changes") : t("save")}
            </Button>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  );
};

export default CreateEditReadyRoster;
